import ArrowRight from "@/assets/icons/ArrowRight";
import WarningIcon from "@/assets/icons/Warning";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import { Box } from "@/components/ui/box";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { T, useT } from "@transifex/react";
import { FlatList } from "react-native";

interface FlexPassCardProps {
  title: string;
  price: string;
  data: Array<{ id: string; text: string }>;
  onPurchase?: () => void;
  showEventRestrictionWarning: boolean;
  isLoading?: boolean;
}

function FlexParkCard({ title, price, data, onPurchase, showEventRestrictionWarning, isLoading }: Readonly<FlexPassCardProps>) {
  const t = useT();
  if (isLoading) return <FlexParkCardSkeleton showEventRestrictionWarning={showEventRestrictionWarning} />;

  return (
    <Card className="py-[24px]">
      <VStack className="gap-[12px]">
        <Text className="font-[700] leading-[22px] text-[16px] text-typography-900">{title}</Text>

        <FlatList
          data={data}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => <Text className="font-[400] leading-[20px] text-[14px] text-typography-900">{`\u2022 ${item.text}`}</Text>}
        />
      </VStack>

      {showEventRestrictionWarning && (
        <HStack className="bg-warning-100 border border-warning-300 gap-[10px] items-center px-[8px] py-[12px] rounded-[6px]">
          <WarningIcon height={21} fill="text-warning-900" width={28} />
          <Box className="flex-1">
            <Text className="font-[400] text-[12px] text-warning-900">
              <T _str="This pass is available for purchase but is not eligible for immediate use." />
            </Text>
          </Box>
        </HStack>
      )}

      {onPurchase && <AppButton icon={ArrowRight} iconPosition="after" onPress={onPurchase} title={t("Purchase - {value}", { value: price })} />}
    </Card>
  );
}

function FlexParkCardSkeleton({ showEventRestrictionWarning }: { showEventRestrictionWarning: boolean }) {
  return (
    <Card className="py-[24px]">
      <VStack className="gap-[12px]">
        <SkeletonText className="h-[22px] w-[160px]" />
        <VStack className="gap-[10px]">
          <SkeletonText className="h-[22px] w-[160px]" />
          <SkeletonText className="h-[22px] w-[120px]" />
        </VStack>
      </VStack>

      {showEventRestrictionWarning && <Skeleton className="h-[48px] w-full rounded-[6px]" />}

      <Skeleton className="h-[40px] rounded-[6px]" />
    </Card>
  );
}

export default FlexParkCard;
